import { css } from "@emotion/react";
import { ContextMenu } from "primereact/contextmenu";
import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router";
import useWebSocket from "react-use-websocket";
import { AuthContextType, useAuth } from "../../auth/auth-provider";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  setAllDialogs,
  setSidebarChats,
  setNotificationCount
} from "../../redux/slices/sidebar";
import { setNameContact } from "../../redux/slices/omnichannel";
import useOmnichannel, {
  FindBotsList,
  FindDialogsOutput
} from "../use-omnichannel";
import Dialogs from "./dialogs";
import FiltersBar from "./filters-bar";
import IdentificationBar from "./identification-bar";
import SearchBarCounter from "./search-bar-counter";
import WorkspaceBar from "./workspace-bar";
import { PinIcon, soundNotification } from "../../assets";

export interface SidebarProps {
  shouldUpdate: boolean;
  onShouldUpdateChange: () => void;
  dialog?: { id: number };
  onDialogClick: (id: number) => void;
}

export interface BotListDropdown {
  label: string;
  value: number;
  disabled?: boolean;
}

type SidebarJsonObject = Record<string, unknown>;

export interface ChatMessageDialogJson extends SidebarJsonObject {
  id: number;
  name: string;
  lastMessageDateTime: string;
  lastMessage: string;
  unreadMessageTotal: number;
  plataform: string;
  avatarUrl?: string;
  fixedAt?: string | null;
}

export interface ChatMessageJson extends SidebarJsonObject {
  unreadRooms: number;
  queueRooms?: number;
  queueUpdated?: boolean;
  dialogs: ChatMessageDialogJson[];
}

type SidebarTab = "rooms" | "queue";

const preserveExistingAvatar = (
  updatedDialogs: ChatMessageDialogJson[],
  previousDialogs: FindDialogsOutput[]
) => {
  const previousMap = new Map(
    previousDialogs.map((dialog) => [dialog.id, dialog])
  );

  return updatedDialogs.map((updatedDialog) => ({
    ...updatedDialog,
    avatarUrl:
      updatedDialog.avatarUrl ?? previousMap.get(updatedDialog.id)?.avatarUrl
  }));
};

const sortDialogs = <
  T extends Pick<FindDialogsOutput, "id" | "lastMessageDateTime" | "fixedAt">
>(
  dialogs: T[]
) =>
  dialogs.sort((a, b) => {
    if (a.fixedAt && b.fixedAt) {
      return (
        new Date(a.fixedAt).getTime() - new Date(b.fixedAt).getTime() ||
        a.id - b.id
      );
    }

    if (a.fixedAt) {
      return -1;
    }

    if (b.fixedAt) {
      return 1;
    }

    return (
      new Date(b.lastMessageDateTime).getTime() -
      new Date(a.lastMessageDateTime).getTime()
    );
  });

const Sidebar: React.FC<SidebarProps> = ({
  shouldUpdate,
  onShouldUpdateChange,
  dialog,
  onDialogClick
}: SidebarProps) => {
  const { user } = useAuth() as AuthContextType;
  const location = useLocation();

  const { findBotsList } = useOmnichannel();

  const {
    findDialogsPageable,
    findQueueDialogsPageable,
    findUnreadRooms,
    findQueueRooms,
    joinQueueDialog,
    markAsRead,
    markAsUnread,
    fixDialog,
    unfixDialog
  } = useOmnichannel();

  const [workspace, setWorkspace] = useState<number | null>(null);
  const [status, setStatus] = useState("OPEN");
  const [bot, setBot] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<SidebarTab>("rooms");
  const [showFilters, setShowFilters] = useState(false);
  const [botsList, setBotsList] = useState<FindBotsList[]>();
  const [botsListForDropdown, setBotsListForDropdown] =
    useState<BotListDropdown[]>();

  const [dialogs, setDialogs] = useState<FindDialogsOutput[]>([]);
  const [unreadRooms, setUnreadRooms] = useState(0);
  const [queueRooms, setQueueRooms] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [canPlaySound, setCanPlaySound] = useState(false);
  const contextMenuRef = useRef<ContextMenu>(null);
  const [contextMenuDialogId, setContextMenuDialogId] = useState<number | null>(
    null
  );

  const [hasRequest, setHasRequest] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const allDialogs = useAppSelector((state) => state.sidebar.allDialogs);
  const limit = 12;
  const [offset, setOffset] = useState(0);
  const dispatch = useAppDispatch();

  const { lastJsonMessage, sendJsonMessage } = useWebSocket<ChatMessageJson>(
    process.env.NODE_ENV === "development"
      ? `ws://${process.env.OMNICHANNEL_URL}/omnichannel-sidebar/${user.id}`
      : `wss://${process.env.OMNICHANNEL_URL}/omnichannel-sidebar/${user.id}`,
    {
      retryOnError: true,
      shouldReconnect: () => true,
      reconnectAttempts: 10,
      reconnectInterval: 5_000,
      heartbeat: {
        interval: 10_000,
        message: `{ "ping": true }`,
        returnMessage: "pong",
        timeout: 60_000
      }
    }
  );

  const [screenMobile, setScreenMobile] = useState(false);
  const sidebarChats = useAppSelector((state) => state.sidebar.sidebarChats);
  const lastMessageSent = useAppSelector(
    (state) => state.sidebar.lastMessageSent
  );

  const [lastId, setLastId] = useState(0);

  const isRoomsTab = activeTab === "rooms";
  const workspaceFilter = workspace ?? undefined;
  const botFilter = bot ?? undefined;

  useEffect(() => {
    sendJsonMessage({
      type: "queue_subscribe",
      dialogIds: activeTab === "queue" ? dialogs.map((dialog) => dialog.id) : []
    });
  }, [activeTab, dialogs, sendJsonMessage]);

  const setFirstDialog = async (dialogsArray: Array<any>) => {
    if (!isRoomsTab) {
      return;
    }

    if (!location?.state?.dialogId) {
      const firstDialogId = dialogsArray[0]?.id;

      if (firstDialogId) {
        await markAsRead(firstDialogId);
        setLastId(firstDialogId);
        onDialogClick(firstDialogId);
      }
    }
  };

  const getBotsList = async () => {
    const workspaces = workspace
      ? [workspace]
      : (user.workspaces?.workspaces ?? []).map((item) => item.id);

    const bots = await findBotsList({
      companyId: user.companyId,
      workspaces
    });

    setBotsList(bots.result);
  };

  const getBotsListForDropdown = () => {
    // eslint-disable-next-line no-negated-condition
    if (!botsList?.length) {
      const emptyBotList: BotListDropdown = {
        label: "Nenhum bot encontrado para este workspace",
        value: 0,
        disabled: true
      };

      setBotsListForDropdown([emptyBotList]);
    } else {
      const botList: BotListDropdown[] = [];

      botsList.forEach((element) => {
        const bot: BotListDropdown = {
          label: element.name,
          value: element.id
        };

        botList.push(bot);
      });

      setBotsListForDropdown(botList);
    }
  };

  const handleFetchData = () => {
    setOffset(0);
    fetchData();
  };

  const handleTabChange = (tab: SidebarTab) => {
    setOffset(0);
    setActiveTab(tab);
  };

  const refreshCounters = async () => {
    const [unread, queue] = await Promise.all([
      findUnreadRooms({
        workspace: workspaceFilter,
        name: search,
        status
      }),
      findQueueRooms({
        workspace: workspaceFilter,
        name: search
      })
    ]);

    setUnreadRooms(unread);
    setQueueRooms(queue);
  };

  const fetchData = async (keepdata?: boolean) => {
    if (hasRequest) {
      return;
    }

    setHasRequest(true);

    try {
      const dataOffset = keepdata ? offset : 0;
      const response = isRoomsTab
        ? await findDialogsPageable({
            workspace: workspaceFilter,
            bot: botFilter,
            name: search,
            status,
            limit,
            offset: dataOffset
          })
        : await findQueueDialogsPageable({
            workspace: workspaceFilter,
            name: search,
            limit,
            offset: dataOffset
          });

      if (isRoomsTab) {
        const dialogIds = response.result.map((dialog: any) => dialog.id);
        dispatch(setAllDialogs(dialogIds));
      }

      setTotalCount(response.total);

      if (keepdata) {
        const concatData = [...dialogs, ...response.result];
        setDialogs(concatData);
      } else {
        setDialogs(response.result);
        setOffset(0);
      }

      await refreshCounters();
    } finally {
      setHasRequest(false);
    }
  };

  const fetchMoreDialogsViaInfiniteScroll = () => {
    if (totalCount > offset) {
      setOffset(offset + limit);
    }
  };

  useEffect(() => {
    const run = async () => {
      setHasRequest(true);

      try {
        const response = isRoomsTab
          ? await findDialogsPageable({
              workspace: workspaceFilter,
              bot: botFilter,
              name: search,
              status,
              limit,
              offset
            })
          : await findQueueDialogsPageable({
              workspace: workspaceFilter,
              name: search,
              limit,
              offset
            });

        setTotalCount(response.total);

        const dialogs = response.result;

        if (isRoomsTab) {
          const dialogIds = dialogs.map((dialog: any) => dialog.id);
          dispatch(setAllDialogs(dialogIds));
        }

        setDialogs(dialogs);

        if (dialogs.length > 0) {
          setFirstDialog(dialogs);
        }

        await refreshCounters();
        getBotsList();
      } finally {
        setHasRequest(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getBotsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace]);

  useEffect(() => {
    if (bot == null || !botsList) {
      return;
    }

    const botExists = botsList.some((item) => item.id === bot);

    if (!botExists) {
      setBot(null);
    }
  }, [bot, botsList]);

  useEffect(() => {
    getBotsListForDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botsList]);

  const requestNotificationPermission = async () => {
    if ("Notification" in window) {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    }
    return false;
  };

  const sendNotification = (message: string) => {
    if (Notification.permission === "granted") {
      new Notification("Nova Mensagem", {
        body: message
      });
    }
  };

  useEffect(() => {
    const initNotifications = async () => {
      await requestNotificationPermission();
    };

    initNotifications();
  }, []);

  useEffect(() => {
    if (lastJsonMessage !== null) {
      if (typeof lastJsonMessage.queueRooms === "number") {
        const updatedQueueRooms = lastJsonMessage.queueRooms;

        setQueueRooms((prevQueueRooms) => {
          const hasNewQueueRoom = updatedQueueRooms > prevQueueRooms;

          if (hasNewQueueRoom && document.hidden) {
            if (canPlaySound && audioRef.current) {
              audioRef.current.currentTime = 0;
              audioRef.current.play().catch(() => {});
            }

            sendNotification(`Nova conversa na fila (${updatedQueueRooms})`);
          }

          return updatedQueueRooms;
        });
      }

      if (lastJsonMessage.queueUpdated && activeTab === "queue") {
        fetchData();
      }

      setUnreadRooms(lastJsonMessage.unreadRooms);

      const updatedDialogs = lastJsonMessage.dialogs;

      if (!isRoomsTab) {
        if (activeTab === "queue" && updatedDialogs.length > 0) {
          setDialogs((prevDialogs) => {
            const dialogsWithAvatar = preserveExistingAvatar(
              updatedDialogs,
              prevDialogs
            );
            const updatedMap = new Map(dialogsWithAvatar.map((d) => [d.id, d]));
            const merged = [
              ...prevDialogs.filter((d) => !updatedMap.has(d.id)),
              ...dialogsWithAvatar
            ];

            return sortDialogs(merged);
          });
        }

        return;
      }

      const prevIds = new Set(dialogs.map((d) => d.id));
      const newDialog = updatedDialogs.find((d) => !prevIds.has(d.id));

      const prevMap = new Map(dialogs.map((d) => [d.id, d]));

      const hasRealNewMessage = updatedDialogs.some((updated) => {
        const prev = prevMap.get(updated.id);

        if (!prev) {
          return (updated.unreadMessageTotal ?? 0) > 0;
        }

        const prevLastMessageDateTime = prev.lastMessageDateTime;
        const currLastMessageDateTime = updated.lastMessageDateTime;

        const lastMessageChanged =
          currLastMessageDateTime !== prevLastMessageDateTime;

        const hasUnread = (updated.unreadMessageTotal ?? 0) > 0;

        return lastMessageChanged && hasUnread;
      });

      setDialogs((prevDialogs) => {
        if (newDialog && document.hidden) {
          if (canPlaySound && audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => {});
          }

          const totalUnread = lastJsonMessage.unreadRooms;

          dispatch(setNotificationCount(totalUnread));
          sendNotification(`Nova conversa (${totalUnread})`);

          const dialogsWithAvatar = preserveExistingAvatar(
            updatedDialogs,
            prevDialogs
          );
          const updatedMap = new Map(dialogsWithAvatar.map((d) => [d.id, d]));
          const merged = [
            ...prevDialogs.filter((d) => !updatedMap.has(d.id)),
            ...dialogsWithAvatar
          ];

          return sortDialogs(merged);
        }

        const dialogsWithAvatar = preserveExistingAvatar(
          updatedDialogs,
          prevDialogs
        );
        const updatedMap = new Map(dialogsWithAvatar.map((d) => [d.id, d]));
        const merged = [
          ...prevDialogs.filter((d) => !updatedMap.has(d.id)),
          ...dialogsWithAvatar
        ];

        return sortDialogs(merged);
      });

      const dialogWithUnread = updatedDialogs.find(
        (dialog) => dialog.unreadMessageTotal > 0
      );

      const newMessages = Boolean(dialogWithUnread);
      const verifyLastMessage = dialogWithUnread?.lastMessage;
      const tabVisible = !document.hidden;

      const deleteEvent = verifyLastMessage === "Mensagem apagada";

      const hasUnreadRooms = lastJsonMessage.unreadRooms > 0;

      if (
        !newDialog &&
        newMessages &&
        hasUnreadRooms &&
        hasRealNewMessage &&
        !deleteEvent &&
        verifyLastMessage !== lastMessageSent &&
        verifyLastMessage != null &&
        !tabVisible
      ) {
        const audio = new Audio(soundNotification);
        audio.play();

        const totalUnread = lastJsonMessage.unreadRooms;

        dispatch(setNotificationCount(totalUnread));
        sendNotification(`Nova mensagem (${totalUnread})`);
      }

      const currentDialog = updatedDialogs.find(
        (dialog) => dialog.id === lastId
      );

      if (currentDialog && currentDialog.unreadMessageTotal > 0) {
        markAsRead(currentDialog.id);

        setDialogs((prevDialogs) =>
          prevDialogs.map((dialog) =>
            dialog.id === currentDialog.id
              ? { ...dialog, unreadMessageTotal: 0 }
              : dialog
          )
        );

        setUnreadRooms((prev) => Math.max(prev - 1, 0));
      }
    }
  }, [activeTab, isRoomsTab, lastJsonMessage]);

  useEffect(() => {
    const run = async () => {
      if (shouldUpdate) {
        await fetchData();

        onShouldUpdateChange();
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldUpdate]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, status, bot, search, workspace]);

  useEffect(() => {
    fetchData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 500) {
        setScreenMobile(true);
      } else {
        setScreenMobile(false);
      }
    };

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const dialogIds = dialogs.map((dialog) => dialog.id);

    if (dialogIds.length > 0 && dialogIds !== allDialogs) {
      if (location.pathname.startsWith("/omnichannel")) return;

      if (allDialogs.length > 0) {
        const firstDialogId = allDialogs[0];

        const updateDialog = async () => {
          await markAsRead(firstDialogId);

          const unreadRooms = await findUnreadRooms({
            workspace: workspaceFilter,
            name: search,
            status
          });

          setUnreadRooms(unreadRooms);
          setDialogs((prevDialogs) =>
            prevDialogs.map((d) =>
              d.id === firstDialogId ? { ...d, unreadMessageTotal: 0 } : d
            )
          );

          onDialogClick(firstDialogId);
        };

        updateDialog();
      }
    }
  }, [allDialogs]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        dispatch(setNotificationCount(0));
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const unlockAudio = () => {
      if (!audioRef.current) return;

      audioRef.current
        .play()
        .then(() => {
          audioRef.current!.pause();
          audioRef.current!.currentTime = 0;
          setCanPlaySound(true);

          window.removeEventListener("click", unlockAudio);
          window.removeEventListener("keydown", unlockAudio);
        })
        .catch(() => {});
    };

    window.addEventListener("click", unlockAudio);
    window.addEventListener("keydown", unlockAudio);

    return () => {
      window.removeEventListener("click", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, []);

  useEffect(() => {
    audioRef.current = new Audio(soundNotification);
  }, []);

  useEffect(() => {
    if (!dialog?.id) return;

    if (!isRoomsTab) {
      return;
    }

    setLastId(dialog.id);

    markAsRead(dialog.id).catch(() => {});

    setDialogs((prev) =>
      prev.map((d) =>
        d.id === dialog.id ? { ...d, unreadMessageTotal: 0 } : d
      )
    );
  }, [dialog?.id, isRoomsTab]);

  const handleQueueDialogClick = async (id: number) => {
    setLastId(id);
    onDialogClick(id);
  };

  const canMarkAsUnread = (item?: FindDialogsOutput) =>
    !item?.unreadMessageTotal;

  const canFixDialog = status !== "CLOSED";

  const handleDialogContextMenu = (
    event: React.MouseEvent<HTMLDivElement>,
    id: number
  ) => {
    const clickedDialog = dialogs.find((item) => item.id === id);

    if (!canMarkAsUnread(clickedDialog) && !canFixDialog) {
      event.preventDefault();
      return;
    }

    setContextMenuDialogId(id);
    contextMenuRef.current?.show(event);
  };

  const contextMenuDialog = dialogs.find(
    (item) => item.id === contextMenuDialogId
  );

  const updateDialogFixedAt = (dialogId: number, fixedAt: string | null) => {
    setDialogs((prev) =>
      sortDialogs(
        prev.map((item) => (item.id === dialogId ? { ...item, fixedAt } : item))
      )
    );
  };

  const contextMenuModel = [
    {
      label: "Marcar como não lida",
      icon: "pi pi-envelope",
      visible: canMarkAsUnread(contextMenuDialog),
      command: () => {
        if (contextMenuDialogId) {
          if (contextMenuDialogId === dialog?.id) {
            setLastId(0);
          }

          markAsUnread(contextMenuDialogId).catch(() => {});
        }
      }
    },
    {
      label: contextMenuDialog?.fixedAt
        ? "Desafixar conversa"
        : "Fixar conversa",
      visible: canFixDialog,
      icon: (
        <img
          src={PinIcon}
          alt="Fixar conversa"
          className="p-menuitem-icon"
          width={18}
          height={18}
          css={css`
            filter: invert(1);
          `}
        />
      ),
      command: () => {
        if (!contextMenuDialogId) {
          return;
        }

        if (contextMenuDialog?.fixedAt) {
          unfixDialog(contextMenuDialogId)
            .then(() => updateDialogFixedAt(contextMenuDialogId, null))
            .catch(() => {});
        } else {
          fixDialog(contextMenuDialogId)
            .then((response) =>
              updateDialogFixedAt(contextMenuDialogId, response.fixedAt)
            )
            .catch(() => {});
        }
      }
    }
  ];

  const renderTabs = () => (
    <div
      css={css`
        display: flex;
        border-bottom: 1px solid #282d35;
      `}
    >
      {[
        { key: "rooms", label: "Salas", count: unreadRooms },
        { key: "queue", label: "Fila", count: queueRooms }
      ].map((tab) => {
        const isActive = activeTab === tab.key;

        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => handleTabChange(tab.key as SidebarTab)}
            css={css`
              align-items: center;
              background: transparent;
              border: none;
              border-bottom: 2px solid ${isActive ? "#d8dde6" : "transparent"};
              color: ${isActive ? "#d8dde6" : "#90a0b7"};
              cursor: pointer;
              display: flex;
              flex: 1;
              font-family: "Poppins";
              font-size: 14px;
              justify-content: space-between;
              padding: 12px;
            `}
          >
            <span>{tab.label}</span>
            <span
              css={css`
                background: #109cf1;
                border-radius: 4px;
                color: #ffffff;
                font-size: 11px;
                line-height: 1;
                min-width: 20px;
                padding: 4px 6px;
                text-align: center;
              `}
            >
              {tab.count}
            </span>
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      {screenMobile ? (
        sidebarChats ? (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              position: relative;
              height: 100%;
              width: 100vw;
              border-radius: 0;
              background-color: #2f343d;
              overflow: hidden;
              color: #90a0b7;
            `}
          >
            <IdentificationBar />
            {renderTabs()}
            <SearchBarCounter
              search={search}
              onSearch={setSearch}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters((prev) => !prev)}
              onRefreshClick={handleFetchData}
            />
            {showFilters && (
              <WorkspaceBar
                workspace={workspace}
                onWorkspaceChange={setWorkspace}
              />
            )}
            {showFilters && isRoomsTab && (
              <FiltersBar
                status={status}
                bot={bot}
                botsList={botsListForDropdown}
                onStatusChange={setStatus}
                onBotsListChange={setBot}
              />
            )}
            <Dialogs
              hasRequest={hasRequest}
              dialog={dialog}
              dialogs={dialogs}
              fetchMoreDialogs={fetchMoreDialogsViaInfiniteScroll}
              onDialogClick={async (id) => {
                if (activeTab === "queue") {
                  await handleQueueDialogClick(id);
                } else {
                  await markAsRead(id);
                  const unreadRooms = await findUnreadRooms({
                    workspace: workspaceFilter,
                    name: search,
                    status
                  });
                  setUnreadRooms(unreadRooms);
                  onDialogClick(id);
                }
                dispatch(setSidebarChats(false));
              }}
            />
          </div>
        ) : null
      ) : (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            position: relative;
            height: 100%;
            width: 340px;
            z-index: 3;
            border-radius: 0px 25px 0px 0px;
            background-color: #2f343d;
            overflow: hidden;
            color: #90a0b7;
          `}
        >
          <IdentificationBar />
          {renderTabs()}
          <SearchBarCounter
            search={search}
            onSearch={setSearch}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters((prev) => !prev)}
            onRefreshClick={handleFetchData}
          />
          {showFilters && (
            <WorkspaceBar
              workspace={workspace}
              onWorkspaceChange={setWorkspace}
            />
          )}
          {showFilters && isRoomsTab && (
            <FiltersBar
              status={status}
              bot={bot}
              botsList={botsListForDropdown}
              onStatusChange={setStatus}
              onBotsListChange={setBot}
            />
          )}
          <ContextMenu
            model={contextMenuModel}
            ref={contextMenuRef}
            css={css`
              &.p-contextmenu {
                width: max-content;
                background: #2f343d;
              }

              .p-menuitem-link {
                background: transparent !important;
              }

              .p-menuitem-text,
              .p-menuitem-icon {
                color: #ffffff !important;
              }
            `}
          />
          <Dialogs
            hasRequest={hasRequest}
            dialog={dialog}
            dialogs={dialogs}
            fetchMoreDialogs={fetchMoreDialogsViaInfiniteScroll}
            onDialogContextMenu={
              isRoomsTab ? handleDialogContextMenu : undefined
            }
            onDialogClick={async (id) => {
              if (activeTab === "queue") {
                await handleQueueDialogClick(id);
              } else {
                await markAsRead(id);
                const unreadRooms = await findUnreadRooms({
                  workspace: workspaceFilter,
                  name: search,
                  status
                });
                setUnreadRooms(unreadRooms);

                if (id !== dialog?.id) {
                  onDialogClick(id);
                }

                setDialogs((prev) =>
                  prev.map((d) =>
                    d.id === id ? { ...d, unreadMessageTotal: 0 } : d
                  )
                );

                setLastId(id);
                dispatch(setNameContact(""));
              }
            }}
          />
        </div>
      )}
    </>
  );
};

export default Sidebar;
